/**
 * Servicio de certificaciones — lee desde Realtime Database, nodo `/certifications`.
 */
import { ref, onValue } from 'firebase/database';

import type { CertificationsData, CertificationsSnapshot, ProviderCertifications } from '../types/domain';

import { db } from './firebaseConfig';

export type CertificationsSubscriptionCallback = (data: CertificationsData | null, error: Error | null) => void;
export type Unsubscribe = () => void;

/**
 * Normaliza el snapshot obtenido de Firebase.
 * Si el snapshot es nulo o inválido, retorna los valores por defecto
 * correspondientes a los datos estáticos iniciales.
 */
export function normalizeCertificationsSnapshot(value: unknown): CertificationsData {
  const defaultData: CertificationsData = {
    gcp: {
      label: 'Google Cloud',
      total: 25,
      order: 1,
      list: [
        { name: 'Cloud Digital Leader', count: 17, order: 1 },
        { name: 'Professional Cloud Architect', count: 9, order: 2 },
        { name: 'Professional Data Engineer', count: 7, order: 3 },
        { name: 'Professional ML Engineer', count: 5, order: 4 },
        { name: 'Associate Cloud Engineer', count: 2, order: 5 },
        { name: 'Professional Cloud Developer', count: 2, order: 6 },
      ],
    },
    aws: {
      label: 'AWS',
      total: 12,
      order: 2,
      list: [
        { name: 'Cloud Practitioner', count: 6, order: 1 },
        { name: 'Solutions Architect Associate', count: 3, order: 2 },
        { name: 'Developer Associate', count: 2, order: 3 },
        { name: 'Solutions Architect Professional', count: 1, order: 4 },
        { name: 'DevOps Engineer Professional', count: 1, order: 5 },
        { name: 'Machine Learning Specialty', count: 1, order: 6 },
      ],
    },
  };

  if (!value || typeof value !== 'object') return defaultData;

  const raw = value as CertificationsSnapshot;
  const result: CertificationsData = {};

  Object.entries(raw).forEach(([providerKey, rawProvider]) => {
    if (!rawProvider) return;

    const label = rawProvider.label ?? providerKey;
    const total = typeof rawProvider.total === 'number' ? rawProvider.total : 0;
    const order = typeof rawProvider.order === 'number' ? rawProvider.order : 999;
    const rawList = rawProvider.list;

    let list: ProviderCertifications['list'] = [];
    if (rawList && typeof rawList === 'object') {
      list = Object.entries(rawList).map(([_, rawItem]) => ({
        name: rawItem?.name ?? '',
        count: typeof rawItem?.count === 'number' ? rawItem.count : 0,
        order: typeof rawItem?.order === 'number' ? rawItem.order : Number.POSITIVE_INFINITY,
      }));
      list = list.filter((item) => item.name).sort((a, b) => a.order - b.order);
    }

    result[providerKey] = {
      label,
      total,
      order,
      list,
    };
  });

  if (Object.keys(result).length === 0) {
    return defaultData;
  }

  return result;
}

/**
 * Se suscribe al nodo `/certifications` y notifica al callback ante cualquier cambio.
 */
export function subscribeToCertifications(cb: CertificationsSubscriptionCallback): Unsubscribe {
  const certsRef = ref(db, '/certifications');
  const unsubscribe = onValue(
    certsRef,
    (snapshot) => {
      try {
        const data = normalizeCertificationsSnapshot(snapshot.val());
        cb(data, null);
      } catch (err) {
        cb(null, err as Error);
      }
    },
    (err) => cb(null, err),
  );
  return unsubscribe;
}
